import React, { useMemo } from 'react'
import { FieldErrors, SubmitHandler, SubmitErrorHandler, useWatch } from 'react-hook-form'
import get from 'lodash/get'
import { ControlledDatePicker } from 'transfix-form/components'
import ControlledCheckboxGroupInput from 'transfix-form/components/ControlledCheckboxGroupInput'
import ControlledMultiSelectGroupInput from 'transfix-form/components/ControlledMultiSelectGroupInput.tsx/ControlledMultiSelectGroupInput'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Button } from 'transfix-ui/components/Button'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import ControlledAutocompleteShipper from 'apps/marketplace/pages/issues/components/ControlledAutocompleteShipper'
import {
  ON_HOLD_TO_DELIVERED_CHECKBOX_OPTIONS,
  TRAILER_STATUS_CHECKBOX_OPTIONS,
  SHIPMENT_STATE_CHECKBOX_OPTIONS,
  LOAD_TYPE_SELECT_OPTIONS,
} from '../constants'
import { TCarrierTrailersFilters } from '../types'
import { TCarrierTrailerFiltersSchema } from './CarrierTrailerFiltersSchema'
import { ControlledAutocompleteFacility } from './components/ControlledAutocompleteFacility'
import { useCarrierTrailerFiltersForm } from './useCarrierTrailerFiltersForm'

const getErrorMessage = <T extends FieldErrors<TCarrierTrailerFiltersSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export interface ICarrierTrailerFiltersFormProps {
  /**
   * @default {}
   */
  defaultValues?: Partial<TCarrierTrailersFilters>
  /**
   * @example (data) => {}
   */
  onSubmitSuccess: SubmitHandler<TCarrierTrailersFilters>
  /**
   * @example (errors) => {}
   */
  onSubmitError?: SubmitErrorHandler<TCarrierTrailersFilters>
  onClearAll?: () => void
}

const useCarrierTrailerFiltersFormStyles = makeStyles(theme => ({
  dateGroup: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  formSection: {
    margin: theme.spacing(1, 3),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    position: 'sticky',
    top: 0,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: brandColors.white,
    zIndex: 10,
    padding: theme.spacing(1, 3),
  },
}))

/**
 * This is the content within the filter modal/side-panel for the trailer management tab on a carrier profile.
 */
const CarrierTrailerFiltersForm = ({
  defaultValues: defaultValuesProp = {},
  onSubmitSuccess,
  onSubmitError = () => {},
  onClearAll = () => {},
}: ICarrierTrailerFiltersFormProps) => {
  const { showMissingTrailers } = defaultValuesProp
  const styles = useCarrierTrailerFiltersFormStyles()

  const defaultValues = useMemo(() => {
    return Object.keys(defaultValuesProp).reduce((acc, keyProp) => {
      const key = keyProp as keyof TCarrierTrailerFiltersSchema
      return {
        ...acc,
        [key]: defaultValuesProp[key] ?? undefined,
      }
    }, {} as Partial<TCarrierTrailerFiltersSchema>)
  }, [defaultValuesProp])

  const { control, errors, handleSubmit, reset, setValue } = useCarrierTrailerFiltersForm({
    defaultValues,
  })

  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)

  const clearAllHandler = () => {
    reset()
    onClearAll()
  }

  const { deliveryDate, facilities, pickupDate, shipperIds } = useWatch({
    control,
    name: ['facilities', 'shipperIds', 'pickupDate', 'deliveryDate'],
    defaultValue: {
      facilities: defaultValues.facilities || [],
      shipperIds: defaultValues.shipperIds || [],
      pickupDate: defaultValues.pickupDate,
      deliveryDate: defaultValues.deliveryDate,
    },
  })

  return (
    <form onSubmit={submitHandler} data-test='IssueFiltersForm'>
      <div className={styles.actions}>
        <Button
          color='tertiary'
          size='large'
          fullWidth
          data-test='submit-button'
          onClick={clearAllHandler}>
          Clear All
        </Button>
        <Button type='submit' color='primary' size='large' fullWidth data-test='submit-button'>
          Update
        </Button>
      </div>
      <section className={styles.formSection}>
        <ControlledTextField
          name='shipmentIds'
          control={control}
          TextInputProps={{
            placeholder: 'Shipment ID #s, separated by commas',
            validationMessage: getErrorMessage(errors, 'shipmentIds'),
            error: !!getErrorMessage(errors, 'shipmentIds'),
          }}
        />
        {!showMissingTrailers && (
          <ControlledTextField
            name='trailerNumbers'
            control={control}
            TextInputProps={{
              placeholder: 'Trailer #s, separated by commas',
              validationMessage: getErrorMessage(errors, 'trailerNumbers'),
              error: !!getErrorMessage(errors, 'trailerNumbers'),
            }}
          />
        )}
        <ControlledAutocompleteShipper
          name='shipperIds'
          control={control}
          AutocompleteShipperProps={{
            multiple: true,
            inputError: !!getErrorMessage(errors, 'shipperIds'),
            inputValidationMessage: getErrorMessage(errors, 'shipperIds'),
          }}
          onChange={() => {
            // The autocomplete options for shipper facilities is dependent on the provided shipper ids, so
            // we reset the facilities value when shipperIds is changed.
            setValue('facilities', null)
          }}
        />
        <ControlledAutocompleteFacility
          shipperApplicationIds={shipperIds || []}
          name='facilities'
          defaultValue={facilities}
          control={control}
          AutocompleteFacilityProps={{
            label: '',
            size: 'small',
            limitTags: 1,
            inputError: !!getErrorMessage(errors, 'facilities'),
            inputValidationMessage: getErrorMessage(errors, 'facilities'),
          }}
        />
        <div className={styles.dateGroup}>
          <ControlledDatePicker
            name='pickupDate'
            control={control}
            defaultValue={null}
            DatePickerProps={{
              placeholder: 'P1 Date',
              clearable: true,
              disabled: !!deliveryDate,
            }}
          />
          <ControlledDatePicker
            name='deliveryDate'
            control={control}
            defaultValue={null}
            DatePickerProps={{
              placeholder: 'D1 Date',
              clearable: true,
              disabled: !!pickupDate,
            }}
          />
        </div>
        <ControlledMultiSelectGroupInput
          name='loadTypes'
          control={control}
          defaultValue={null}
          options={LOAD_TYPE_SELECT_OPTIONS}
          InputMultiSelectProps={{
            placeholder: 'Select Load Type',
            clearable: true,
          }}
        />
      </section>
      {!showMissingTrailers && (
        <section className={styles.formSection}>
          <Typography variant='subtitle2'>Trailer Location</Typography>
          <ControlledCheckboxGroupInput
            name='trailerStates'
            control={control}
            options={TRAILER_STATUS_CHECKBOX_OPTIONS}
          />
        </section>
      )}
      <section className={styles.formSection}>
        <Typography variant='subtitle2'>Shipment State</Typography>
        <ControlledCheckboxGroupInput
          name='shipmentStates'
          control={control}
          options={
            showMissingTrailers
              ? ON_HOLD_TO_DELIVERED_CHECKBOX_OPTIONS
              : SHIPMENT_STATE_CHECKBOX_OPTIONS
          }
        />
      </section>
    </form>
  )
}

export default CarrierTrailerFiltersForm
